import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, TrendingUp, DollarSign, Award, 
  Settings, Download, RefreshCw, Check, Clock
} from 'lucide-react';

export const AdminReferralsPage = () => {
  const [overview, setOverview] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [configForm, setConfigForm] = useState({
    level1_reward: 0,
    level2_reward: 0,
    level3_reward: 0,
    reward_currency: 'MTR',
    min_payout_threshold: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [overviewRes, referralsRes, rewardsRes, configRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/referrals/overview`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/referrals/list?limit=50`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/referrals/rewards?limit=50`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/referrals/config`, { credentials: 'include' })
      ]);

      if (overviewRes.ok) {
        const data = await overviewRes.json();
        setOverview(data);
      }

      if (referralsRes.ok) {
        const data = await referralsRes.json();
        setReferrals(data.referrals || []);
      }

      if (rewardsRes.ok) {
        const data = await rewardsRes.json();
        setRewards(data.rewards || []);
      }

      if (configRes.ok) {
        const data = await configRes.json();
        setConfig(data);
        setConfigForm({
          level1_reward: data.level1_reward || 0,
          level2_reward: data.level2_reward || 0,
          level3_reward: data.level3_reward || 0,
          reward_currency: data.reward_currency || 'MTR',
          min_payout_threshold: data.min_payout_threshold || 0
        });
      }
    } catch (error) {
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/referrals/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(configForm)
      });

      if (res.ok) {
        toast.success('Configuration updated successfully');
        setShowConfigDialog(false);
        loadData();
      } else {
        toast.error('Failed to update configuration');
      }
    } catch (error) {
      toast.error('Failed to update configuration');
    }
  };

  const processRewardPayout = async (rewardId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/referrals/rewards/${rewardId}/payout`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success('Reward payout processed');
        loadData();
      } else {
        toast.error('Failed to process payout');
      }
    } catch (error) {
      toast.error('Failed to process payout');
    }
  };

  const exportData = () => {
    const csvContent = "data:text/csv;charset=utf-8," 
      + "Referrer,Referred User,Status,Level,Date\n"
      + referrals.map(r => `${r.referrer_username},${r.referred_username},${r.status},${r.level},${r.created_at}`).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "referrals_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Data exported successfully');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Referral Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Monitor and manage the platform referral program
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportData}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setShowConfigDialog(true)}>
              <Settings className="h-4 w-4 mr-2" />
              Configure
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Referrals</p>
                  <p className="text-3xl font-bold mt-1">{overview?.total_referrals || 0}</p>
                </div>
                <Users className="h-10 w-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Referrals</p>
                  <p className="text-3xl font-bold text-green-600 mt-1">{overview?.active_referrals || 0}</p>
                </div>
                <TrendingUp className="h-10 w-10 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Rewards</p>
                  <p className="text-3xl font-bold text-indigo-600 mt-1">
                    {overview?.total_rewards_paid?.toFixed(0) || 0}
                  </p>
                </div>
                <DollarSign className="h-10 w-10 text-indigo-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Conversion Rate</p>
                  <p className="text-3xl font-bold text-purple-600 mt-1">
                    {overview?.conversion_rate?.toFixed(1) || 0}%
                  </p>
                </div>
                <Award className="h-10 w-10 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="referrals" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="referrals">
              <Users className="h-4 w-4 mr-2" />
              All Referrals ({referrals.length})
            </TabsTrigger>
            <TabsTrigger value="rewards">
              <Award className="h-4 w-4 mr-2" />
              Rewards ({rewards.length})
            </TabsTrigger>
            <TabsTrigger value="analytics">
              <TrendingUp className="h-4 w-4 mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Referrals Tab */}
          <TabsContent value="referrals">
            <Card>
              <CardHeader>
                <CardTitle>Referral List</CardTitle>
                <CardDescription>All platform referral relationships</CardDescription>
              </CardHeader>
              <CardContent>
                {referrals.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-muted-foreground">No referrals yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Referrer</TableHead>
                          <TableHead>Referred User</TableHead>
                          <TableHead>Level</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {referrals.map((ref, index) => (
                          <TableRow key={ref.referral_id || index}>
                            <TableCell className="font-medium">
                              {ref.referrer_username || ref.referrer_id?.substring(0, 8)}
                            </TableCell>
                            <TableCell>
                              {ref.referred_username || ref.referred_id?.substring(0, 8)}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">Level {ref.level || 1}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant={ref.status === 'active' ? 'default' : 'secondary'}>
                                {ref.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {ref.created_at?.substring(0, 10)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rewards Tab */}
          <TabsContent value="rewards">
            <Card>
              <CardHeader>
                <CardTitle>Reward Payouts</CardTitle>
                <CardDescription>All referral reward transactions</CardDescription>
              </CardHeader>
              <CardContent>
                {rewards.length === 0 ? (
                  <div className="text-center py-12">
                    <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-muted-foreground">No rewards issued yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Currency</TableHead>
                          <TableHead>Trigger</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rewards.map((reward, index) => (
                          <TableRow key={reward.reward_id || index}>
                            <TableCell className="font-medium">
                              {reward.user_username || reward.user_id?.substring(0, 8)}
                            </TableCell>
                            <TableCell className="font-bold text-indigo-600">
                              {reward.reward_amount}
                            </TableCell>
                            <TableCell>{reward.reward_type}</TableCell>
                            <TableCell className="text-sm">
                              {reward.trigger_event || 'Referral'}
                            </TableCell>
                            <TableCell>
                              <Badge variant={
                                reward.status === 'paid' ? 'default' :
                                reward.status === 'pending' ? 'secondary' : 'outline'
                              }>
                                {reward.status === 'paid' && <Check className="h-3 w-3 mr-1" />}
                                {reward.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                                {reward.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {reward.created_at?.substring(0, 10)}
                            </TableCell>
                            <TableCell className="text-right">
                              {reward.status === 'pending' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => processRewardPayout(reward.reward_id)}
                                >
                                  Process Payout
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <Card>
              <CardHeader>
                <CardTitle>Referral Analytics</CardTitle>
                <CardDescription>Performance insights and trends</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Top Referrers</h3>
                      <p className="text-sm text-muted-foreground">
                        {overview?.top_referrers?.length || 0} active users
                      </p>
                      {overview?.top_referrers?.slice(0, 5).map((ref, idx) => (
                        <div key={idx} className="flex justify-between items-center mt-2">
                          <span className="text-sm">{ref.username || 'User'}</span>
                          <Badge>{ref.count} referrals</Badge>
                        </div>
                      ))}
                    </div>

                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Referral Sources</h3>
                      <div className="space-y-2 mt-4">
                        <div className="flex justify-between">
                          <span className="text-sm">Direct Links</span>
                          <span className="font-medium">
                            {overview?.source_breakdown?.direct || 0}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm">Social Media</span>
                          <span className="font-medium">
                            {overview?.source_breakdown?.social || 0}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm">QR Codes</span>
                          <span className="font-medium">
                            {overview?.source_breakdown?.qr || 0}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h3 className="font-semibold mb-4">Reward Configuration</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-sm text-muted-foreground">Level 1 Reward</Label>
                        <p className="text-2xl font-bold text-indigo-600">
                          {config?.level1_reward || 0} {config?.reward_currency}
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Level 2 Reward</Label>
                        <p className="text-2xl font-bold text-indigo-600">
                          {config?.level2_reward || 0} {config?.reward_currency}
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Level 3 Reward</Label>
                        <p className="text-2xl font-bold text-indigo-600">
                          {config?.level3_reward || 0} {config?.reward_currency}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Configuration Dialog */}
        <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Referral Program Configuration</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Level 1 Reward (Direct Referral)</Label>
                <Input
                  type="number"
                  value={configForm.level1_reward}
                  onChange={(e) => setConfigForm({...configForm, level1_reward: parseFloat(e.target.value)})}
                  placeholder="100"
                />
              </div>
              <div className="space-y-2">
                <Label>Level 2 Reward (2nd Tier)</Label>
                <Input
                  type="number"
                  value={configForm.level2_reward}
                  onChange={(e) => setConfigForm({...configForm, level2_reward: parseFloat(e.target.value)})}
                  placeholder="50"
                />
              </div>
              <div className="space-y-2">
                <Label>Level 3 Reward (3rd Tier)</Label>
                <Input
                  type="number"
                  value={configForm.level3_reward}
                  onChange={(e) => setConfigForm({...configForm, level3_reward: parseFloat(e.target.value)})}
                  placeholder="25"
                />
              </div>
              <div className="space-y-2">
                <Label>Reward Currency</Label>
                <Input
                  value={configForm.reward_currency}
                  onChange={(e) => setConfigForm({...configForm, reward_currency: e.target.value})}
                  placeholder="MTR"
                />
              </div>
              <div className="space-y-2">
                <Label>Minimum Payout Threshold</Label>
                <Input
                  type="number"
                  value={configForm.min_payout_threshold}
                  onChange={(e) => setConfigForm({...configForm, min_payout_threshold: parseFloat(e.target.value)})}
                  placeholder="10"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfigDialog(false)}>Cancel</Button>
              <Button onClick={updateConfig}>Save Configuration</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminReferralsPage;
